import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';

const NewInvoicePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [formData, setFormData] = useState({
    clientId: '',
    contractId: '',
    invoiceNumber: '', // Número da fatura (opcional)
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    periodStart: '',
    periodEnd: '',
    items: [],
    notes: ''
  });

  const [currentItem, setCurrentItem] = useState({
    vehicleId: '',
    description: '',
    unit: 'UND/MÊS',
    quantity: 1,
    unitPrice: 0,
    totalPrice: 0,
    itemType: 'rental'
  });

  useEffect(() => {
    loadClients();
    loadVehicles();
  }, []);

  useEffect(() => {
    if (formData.clientId) {
      loadContracts(formData.clientId);
    } else {
      // Limpar contratos quando nenhum cliente está selecionado
      setContracts([]);
    }
  }, [formData.clientId]);

  const loadClients = async () => {
    try {
      const response = await apiService.getClients();
      setClients(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const loadContracts = async (clientId) => {
    try {
      setLoadingContracts(true);
      const response = await apiService.getContracts({ clientId });
      setContracts(response.data || []);
      console.log('Contratos carregados:', response.data); // Debug
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
      setContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await apiService.getVehicles();
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
    }
  };

  const handleAddItem = () => {
    if (!currentItem.description || !currentItem.unitPrice) {
      alert('Preencha descrição e preço unitário');
      return;
    }

    const newItem = {
      ...currentItem,
      item_number: `${formData.items.length + 1}.0`,
      total_price: parseFloat(currentItem.totalPrice),
      unit_price: parseFloat(currentItem.unitPrice),
      vehicle_id: currentItem.vehicleId || null
    };

    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });

    setCurrentItem({
      vehicleId: '',
      description: '',
      unit: 'UND/MÊS',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
      itemType: 'rental'
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateItemTotal = (quantity, unitPrice) => {
    return (parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.items.length === 0) {
      alert('Adicione pelo menos um item à fatura');
      return;
    }

    try {
      setLoading(true);
      const invoiceData = {
        contractId: formData.contractId || null,
        clientId: formData.clientId,
        invoiceNumber: formData.invoiceNumber || null, // Número manual (opcional)
        issueDate: formData.issueDate,
        dueDate: formData.dueDate,
        periodStart: formData.periodStart,
        periodEnd: formData.periodEnd,
        items: formData.items,
        notes: formData.notes
      };

      const response = await apiService.createInvoice(invoiceData);
      alert('✅ Fatura criada com sucesso!');
      navigate(`/invoices/${response.data.id}`);
    } catch (error) {
      alert('Erro ao criar fatura: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const totalValue = formData.items.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            📄 Nova Fatura
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Criar fatura avulsa ou vinculada a contrato
          </p>
        </div>
        <button
          onClick={() => navigate('/invoices')}
          className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          ← Voltar
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Dados Básicos */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Dados Básicos
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Cliente *
              </label>
              <select
                required
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value, contractId: '' })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Selecione um cliente</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Contrato (opcional)
              </label>
              <select
                value={formData.contractId}
                onChange={(e) => setFormData({ ...formData, contractId: e.target.value })}
                className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white ${
                  !formData.clientId || loadingContracts
                    ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed opacity-60'
                    : 'bg-white dark:bg-gray-700 cursor-pointer'
                }`}
                disabled={!formData.clientId || loadingContracts}
              >
                <option value="">
                  {loadingContracts
                    ? 'Carregando contratos...'
                    : !formData.clientId
                    ? 'Selecione um cliente primeiro'
                    : 'Fatura avulsa'}
                </option>
                {contracts.map(contract => (
                  <option key={contract.id} value={contract.id}>
                    Contrato #{contract.id} - {contract.vehicle_model}
                  </option>
                ))}
              </select>
              {!formData.clientId && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  ⚠️ Selecione um cliente para ver os contratos disponíveis
                </p>
              )}
              {formData.clientId && !loadingContracts && contracts.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Este cliente não possui contratos ativos
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Número da Fatura (opcional)
              </label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Ex: ABC1234-001/12 ou deixe vazio para gerar automaticamente"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Deixe em branco para gerar automaticamente no padrão do sistema
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Data de Emissão *
              </label>
              <input
                type="date"
                required
                value={formData.issueDate}
                onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Período - Início *
              </label>
              <input
                type="date"
                required
                value={formData.periodStart}
                onChange={(e) => setFormData({ ...formData, periodStart: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Período - Fim *
              </label>
              <input
                type="date"
                required
                value={formData.periodEnd}
                onChange={(e) => setFormData({ ...formData, periodEnd: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Data de Vencimento *
              </label>
              <input
                type="date"
                required
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Observações
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Observações adicionais..."
            />
          </div>
        </div>

        {/* Adicionar Itens */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Adicionar Item
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Descrição
              </label>
              <input
                type="text"
                value={currentItem.description}
                onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Ex: Locação de Escavadeira"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Veículo
              </label>
              <select
                value={currentItem.vehicleId}
                onChange={(e) => setCurrentItem({ ...currentItem, vehicleId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Nenhum</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.model} - {vehicle.license_plate}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Quantidade
              </label>
              <input
                type="number"
                step="0.01"
                value={currentItem.quantity}
                onChange={(e) => {
                  const quantity = e.target.value;
                  setCurrentItem({
                    ...currentItem,
                    quantity,
                    totalPrice: calculateItemTotal(quantity, currentItem.unitPrice)
                  });
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Preço Unit.
              </label>
              <input
                type="number"
                step="0.01"
                value={currentItem.unitPrice}
                onChange={(e) => {
                  const unitPrice = e.target.value;
                  setCurrentItem({
                    ...currentItem,
                    unitPrice,
                    totalPrice: calculateItemTotal(currentItem.quantity, unitPrice)
                  });
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Total
              </label>
              <input
                type="text"
                value={formatCurrency(currentItem.totalPrice)}
                readOnly
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleAddItem}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              + Adicionar Item
            </button>
          </div>
        </div>

        {/* Lista de Itens */}
        {formData.items.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Itens da Fatura ({formData.items.length})
            </h2>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Descrição</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Qtd</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Preço Unit.</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Total</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {formData.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.description}</td>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-center">{item.quantity}</td>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="px-4 py-2 text-sm font-bold text-gray-900 dark:text-white text-right">{formatCurrency(item.total_price)}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          ❌
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="px-4 py-2 text-right text-sm font-bold text-gray-900 dark:text-white">
                      Total:
                    </td>
                    <td className="px-4 py-2 text-right text-lg font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(totalValue)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/invoices')}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || formData.items.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Criando...' : '✅ Criar Fatura'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewInvoicePage;
